import { useState, useEffect, useCallback } from "react";
import api from "../api";
import { useTheme } from "../components/Theme";

const ORANGE = "#FF6B2B";

const STATUS = {
  pendente:  { label: "Pendente",   color: "#f59e0b" },
  pago:      { label: "Pago",       color: "#10b981" },
  vencido:   { label: "Vencido",    color: "#ef4444" },
  cancelado: { label: "Cancelado",  color: "#8E8E93" },
};

const FORMAS = ["pix","boleto","transferencia","cartao","dinheiro","outro"];
const FORMA_LABEL = { pix:"PIX", boleto:"Boleto", transferencia:"Transferência", cartao:"Cartão", dinheiro:"Dinheiro", outro:"Outro" };
const CATEGORIAS_SAIDA  = ["Salários","Fornecedores","Ferramentas/Software","Marketing","Impostos","Aluguel","Outros"];
const CATEGORIAS_ENTRADA = ["Mensalidade","Projeto","Consultoria","Outros"];

function fmt(v) { return `R$${Number(v||0).toLocaleString("pt-BR",{minimumFractionDigits:2})}` ; }
function fmtData(d) { return d ? new Date(d).toLocaleDateString("pt-BR") : "—"; }

// Primeiro e último dia do mês atual
function mesAtual() {
  const hoje = new Date();
  const de  = new Date(hoje.getFullYear(), hoje.getMonth(), 1).toISOString().slice(0,10);
  const ate = new Date(hoje.getFullYear(), hoje.getMonth()+1, 0).toISOString().slice(0,10);
  return { de, ate };
}

function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.pendente;
  return <span style={{ background:s.color+"22", color:s.color, border:`1px solid ${s.color}44`, borderRadius:6, fontSize:11, fontWeight:700, padding:"3px 10px" }}>{s.label}</span>;
}

function MiniBar({ entradas, saidas, max }) {
  const { theme } = useTheme();
  return (
    <div style={{ display:"flex", gap:3, alignItems:"flex-end", height:50 }}>
      {[{v:entradas,c:"#10b981"},{v:saidas,c:"#ef4444"}].map(({v,c},i)=>(
        <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", justifyContent:"flex-end" }}>
          <div style={{ background:c, borderRadius:"4px 4px 0 0", height:`${max?Math.max((v/max)*100,4):4}%`, minHeight:4, transition:"height .4s" }}/>
        </div>
      ))}
    </div>
  );
}

// Modal lançamento
function ModalLancamento({ lanc, tipo: tipoDefault, onClose, onSave, clientes }) {
  const { theme } = useTheme();
  const isNew = !lanc?._id;
  const [form, setForm] = useState(lanc || {
    tipo: tipoDefault || "entrada",
    subtipo: tipoDefault === "entrada" ? "nota_emitida" : "nota_recebida",
    descricao: "", valor: "", status: "pendente", formaPagto: "pix",
    dataEmissao: new Date().toISOString().slice(0,10),
    dataVencimento: "", dataPagamento: "",
    clienteNome: "", numeroNota: "",
    categoria: tipoDefault === "entrada" ? "Mensalidade" : "Outros",
    observacoes: "",
  });
  const [load, setLoad] = useState(false);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const inp = { width:"100%", boxSizing:"border-box", background:theme.bgInput, border:`1.5px solid ${theme.border}`, borderRadius:10, padding:"9px 12px", color:theme.text, fontSize:13, outline:"none", colorScheme:theme.name, fontFamily:"'Inter',sans-serif", transition:"border-color .15s" };

  const salvar = async () => {
    if (!form.descricao.trim() || !form.valor) return alert("Descrição e valor são obrigatórios");
    setLoad(true);
    try { await onSave(form); }
    catch(e) { alert(e.response?.data?.erro || "Erro ao salvar"); }
    finally { setLoad(false); }
  };

  const isEntrada = form.tipo === "entrada";
  const cats = isEntrada ? CATEGORIAS_ENTRADA : CATEGORIAS_SAIDA;

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.75)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }} onClick={onClose}>
      <div style={{ background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:20, padding:32, width:"100%", maxWidth:520, maxHeight:"90vh", overflowY:"auto", boxShadow:"0 24px 80px rgba(0,0,0,.6)" }} onClick={e=>e.stopPropagation()}>

        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:22 }}>
          <div>
            <div style={{ fontSize:11, color:isEntrada?"#10b981":"#ef4444", fontWeight:700, letterSpacing:1, textTransform:"uppercase", marginBottom:4 }}>
              {isEntrada ? "💰 NOTA EMITIDA / ENTRADA" : "💸 NOTA RECEBIDA / SAÍDA"}
            </div>
            <h2 style={{ margin:0, fontSize:18, fontWeight:700, color:theme.text }}>{isNew?"Novo lançamento":"Editar lançamento"}</h2>
          </div>
          <button onClick={onClose} style={{ background:theme.bgInput, border:"none", borderRadius:8, width:32, height:32, color:theme.textMuted, fontSize:18, cursor:"pointer" }}>×</button>
        </div>

        {/* Tipo */}
        <div style={{ display:"flex", gap:8, marginBottom:20 }}>
          {[["entrada","💰 Entrada / A receber","#10b981"],["saida","💸 Saída / A pagar","#ef4444"]].map(([val,label,cor])=>(
            <button key={val} onClick={()=>{ set("tipo",val); set("subtipo",val==="entrada"?"nota_emitida":"nota_recebida"); set("categoria",val==="entrada"?"Mensalidade":"Outros"); }}
              style={{ flex:1, padding:"9px 0", borderRadius:10, fontSize:13, fontWeight:600, cursor:"pointer", border:`2px solid ${form.tipo===val?cor:theme.border}`, background:form.tipo===val?cor+"18":"transparent", color:form.tipo===val?cor:theme.textMuted, transition:"all .15s" }}>
              {label}
            </button>
          ))}
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14 }}>
          <div style={{ gridColumn:"span 2" }}>
            <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:5, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>Descrição *</label>
            <input value={form.descricao} onChange={e=>set("descricao",e.target.value)} style={inp} placeholder="Ex: Mensalidade cliente X — Junho/2026"
              onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}/>
          </div>
          <div>
            <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:5, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>Valor (R$) *</label>
            <input type="number" value={form.valor} onChange={e=>set("valor",e.target.value)} style={inp} placeholder="0,00"
              onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}/>
          </div>
          <div>
            <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:5, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>Nº Nota / Referência</label>
            <input value={form.numeroNota||""} onChange={e=>set("numeroNota",e.target.value)} style={inp} placeholder="001/2026"
              onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}/>
          </div>
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:14, marginBottom:14 }}>
          <div>
            <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:5, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>Status</label>
            <select value={form.status} onChange={e=>set("status",e.target.value)} style={{ ...inp, appearance:"auto" }}
              onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}>
              {Object.entries(STATUS).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:5, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>Forma de pagamento</label>
            <select value={form.formaPagto} onChange={e=>set("formaPagto",e.target.value)} style={{ ...inp, appearance:"auto" }}
              onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}>
              {FORMAS.map(f=><option key={f} value={f}>{FORMA_LABEL[f]}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:5, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>Categoria</label>
            <select value={form.categoria||""} onChange={e=>set("categoria",e.target.value)} style={{ ...inp, appearance:"auto" }}
              onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}>
              {cats.map(c=><option key={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:14, marginBottom:14 }}>
          {[["Data emissão","dataEmissao"],["Data vencimento","dataVencimento"],["Data pagamento","dataPagamento"]].map(([label,key])=>(
            <div key={key}>
              <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:5, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>{label}</label>
              <input type="date" value={form[key]||""} onChange={e=>set(key,e.target.value)} style={inp}
                onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}/>
            </div>
          ))}
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:20 }}>
          <div>
            <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:5, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>Cliente (opcional)</label>
            <input value={form.clienteNome||""} onChange={e=>set("clienteNome",e.target.value)} style={inp} placeholder="Nome do cliente"
              onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}/>
          </div>
          <div>
            <label style={{ fontSize:11, color:theme.textMuted, display:"block", marginBottom:5, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase" }}>Observações</label>
            <input value={form.observacoes||""} onChange={e=>set("observacoes",e.target.value)} style={inp} placeholder="Notas adicionais"
              onFocus={e=>e.target.style.borderColor=ORANGE} onBlur={e=>e.target.style.borderColor=theme.border}/>
          </div>
        </div>

        <div style={{ display:"flex", gap:10 }}>
          <button onClick={salvar} disabled={load} style={{ flex:1, background:`linear-gradient(135deg,${isEntrada?"#10b981":"#ef4444"},${isEntrada?"#34d399":"#f87171"})`, border:"none", borderRadius:12, padding:"13px 0", color:"#fff", fontWeight:700, fontSize:15, cursor:"pointer", fontFamily:"'Inter',sans-serif", opacity:load?.7:1 }}>
            {load?"Salvando...":"Salvar lançamento"}
          </button>
          {!isNew && <button onClick={()=>onSave({...form,_deletar:true})} style={{ background:"#ef444418", border:"1px solid #ef444433", borderRadius:12, padding:"13px 18px", color:"#ef4444", fontWeight:600, fontSize:14, cursor:"pointer" }}>Excluir</button>}
        </div>
      </div>
    </div>
  );
}

// Tabela de lançamentos
function TabelaLancamentos({ lancamentos, onEdit, onPagar, tipo }) {
  const { theme } = useTheme();
  const isEntrada = tipo === "entrada";
  const cor = isEntrada ? "#10b981" : "#ef4444";

  if (lancamentos.length === 0) return (
    <div style={{ textAlign:"center", padding:"40px 0", color:theme.textMuted }}>
      <div style={{ fontSize:32, marginBottom:10 }}>{isEntrada?"💰":"💸"}</div>
      <div style={{ fontSize:14, fontWeight:600, color:theme.text, marginBottom:4 }}>Nenhum lançamento encontrado</div>
      <div style={{ fontSize:13 }}>Ajuste o período ou adicione um novo lançamento</div>
    </div>
  );

  const total = lancamentos.reduce((a,l)=>a+Number(l.valor||0),0);
  const pago  = lancamentos.filter(l=>l.status==="pago").reduce((a,l)=>a+Number(l.valor||0),0);

  return (
    <div>
      <div style={{ display:"flex", gap:16, marginBottom:16, flexWrap:"wrap" }}>
        {[["Total",fmt(total),cor],[isEntrada?"Recebido":"Pago",fmt(pago),"#10b981"],[isEntrada?"A receber":"A pagar",fmt(total-pago),"#f59e0b"]].map(([label,val,c])=>(
          <div key={label} style={{ background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:10, padding:"10px 16px", flexShrink:0 }}>
            <div style={{ fontSize:11, color:theme.textMuted, fontWeight:500, marginBottom:3 }}>{label}</div>
            <div style={{ fontSize:18, fontWeight:800, color:c, fontFamily:"'Inter',sans-serif" }}>{val}</div>
          </div>
        ))}
      </div>

      <div style={{ background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:12, overflow:"hidden" }}>
        <table style={{ width:"100%", borderCollapse:"collapse" }}>
          <thead>
            <tr style={{ background:theme.bgInput }}>
              {["Descrição","Nº Nota","Cliente","Categoria","Forma Pgto","Emissão","Vencimento","Valor","Status",""].map((h,i)=>(
                <th key={i} style={{ padding:"10px 14px", fontSize:11, color:theme.textMuted, fontWeight:600, letterSpacing:".05em", textTransform:"uppercase", textAlign:i>=7?"center":"left", borderRight:`1px solid ${theme.border}`, whiteSpace:"nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lancamentos.map(l=>{
              const vencido = l.dataVencimento && new Date(l.dataVencimento) < new Date() && l.status === "pendente";
              return (
                <tr key={l._id} style={{ borderBottom:`1px solid ${theme.border}`, cursor:"pointer" }}
                  onMouseEnter={e=>e.currentTarget.style.background=theme.bgInput}
                  onMouseLeave={e=>e.currentTarget.style.background="transparent"}
                  onClick={()=>onEdit(l)}>
                  <td style={{ padding:"11px 14px", fontSize:13, fontWeight:600, color:theme.text, maxWidth:200 }}>
                    <div style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{l.descricao}</div>
                    {l.observacoes && <div style={{ fontSize:11, color:theme.textMuted, marginTop:2 }}>{l.observacoes}</div>}
                  </td>
                  <td style={{ padding:"11px 14px", fontSize:12, color:theme.textMuted }}>{l.numeroNota||"—"}</td>
                  <td style={{ padding:"11px 14px", fontSize:12, color:theme.textSub }}>{l.clienteNome||"—"}</td>
                  <td style={{ padding:"11px 14px", fontSize:12, color:theme.textMuted }}>{l.categoria||"—"}</td>
                  <td style={{ padding:"11px 14px", fontSize:12, color:theme.textMuted }}>{FORMA_LABEL[l.formaPagto]||"—"}</td>
                  <td style={{ padding:"11px 14px", fontSize:12, color:theme.textMuted }}>{fmtData(l.dataEmissao)}</td>
                  <td style={{ padding:"11px 14px", fontSize:12, color:vencido?"#ef4444":theme.textMuted, fontWeight:vencido?700:400 }}>
                    {fmtData(l.dataVencimento)}{vencido?" ⚠️":""}
                  </td>
                  <td style={{ padding:"11px 14px", fontSize:14, fontWeight:800, color:cor, textAlign:"right" }}>{fmt(l.valor)}</td>
                  <td style={{ padding:"11px 14px", textAlign:"center" }}><StatusBadge status={l.status}/></td>
                  <td style={{ padding:"11px 10px", textAlign:"center" }}>
                    {l.status === "pendente" && (
                      <button onClick={e=>{ e.stopPropagation(); onPagar(l._id); }}
                        style={{ background:cor+"22", border:`1px solid ${cor}44`, borderRadius:6, padding:"4px 10px", color:cor, fontSize:11, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap" }}>
                        {isEntrada?"✓ Receber":"✓ Pagar"}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// DRE
function DRE({ resumo, theme }) {
  if (!resumo) return null;
  const linhas = [
    ["Receita bruta", resumo.totalEntradas, "#10b981", false],
    ["(-) Deduções / Cancelamentos", 0, theme.textMuted, false],
    ["= Receita líquida", resumo.totalEntradas, "#10b981", true],
    ["(-) Despesas operacionais", resumo.totalSaidas, "#ef4444", false],
    ["= Resultado operacional", resumo.totalEntradas - resumo.totalSaidas, resumo.totalEntradas - resumo.totalSaidas >= 0 ? "#10b981" : "#ef4444", true],
    ["Valores já recebidos", resumo.totalRecebido, "#10b981", false],
    ["Valores já pagos", resumo.totalPago, "#ef4444", false],
    ["= Lucro líquido realizado", resumo.lucroLiquido, resumo.lucroLiquido >= 0 ? "#10b981" : "#ef4444", true],
  ];
  return (
    <div style={{ background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:12, overflow:"hidden" }}>
      <div style={{ padding:"14px 20px", borderBottom:`1px solid ${theme.border}`, background:theme.bgInput }}>
        <span style={{ fontSize:13, fontWeight:700, color:theme.text }}>📋 DRE — Demonstração do Resultado</span>
      </div>
      {linhas.map(([label, valor, cor, destaque], i) => (
        <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"11px 20px", borderBottom:`1px solid ${theme.border}`, background:destaque?cor+"08":"transparent" }}>
          <span style={{ fontSize:13, fontWeight:destaque?700:400, color:destaque?theme.text:theme.textSub }}>{label}</span>
          <span style={{ fontSize:14, fontWeight:800, color:cor, fontFamily:"'Inter',sans-serif" }}>{fmt(valor)}</span>
        </div>
      ))}
    </div>
  );
}

// Gráfico de barras simples
function GraficoFluxo({ dados, theme }) {
  if (!dados?.length) return null;
  const maxVal = Math.max(...dados.map(d => Math.max(d.entradas, d.saidas)), 1);
  return (
    <div style={{ background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:12, padding:20 }}>
      <div style={{ fontSize:13, fontWeight:700, color:theme.text, marginBottom:16 }}>📊 Fluxo de Caixa — Últimos 6 meses</div>
      <div style={{ display:"flex", gap:4, alignItems:"flex-end", height:120, marginBottom:8 }}>
        {dados.map((d,i) => {
          const hE = Math.max((d.entradas/maxVal)*100, 2);
          const hS = Math.max((d.saidas/maxVal)*100, 2);
          return (
            <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:3 }}>
              <div style={{ display:"flex", gap:2, alignItems:"flex-end", width:"100%", height:100 }}>
                <div style={{ flex:1, background:"#10b981", borderRadius:"4px 4px 0 0", height:`${hE}%`, minHeight:3, transition:"height .4s" }} title={`Entradas: ${fmt(d.entradas)}`}/>
                <div style={{ flex:1, background:"#ef4444", borderRadius:"4px 4px 0 0", height:`${hS}%`, minHeight:3, transition:"height .4s" }} title={`Saídas: ${fmt(d.saidas)}`}/>
              </div>
              <div style={{ fontSize:10, color:theme.textMuted, textAlign:"center" }}>{d.label}</div>
            </div>
          );
        })}
      </div>
      <div style={{ display:"flex", gap:16, justifyContent:"center" }}>
        {[["#10b981","Entradas"],["#ef4444","Saídas"]].map(([cor,label])=>(
          <div key={label} style={{ display:"flex", alignItems:"center", gap:5, fontSize:12, color:theme.textMuted }}>
            <div style={{ width:10, height:10, borderRadius:2, background:cor }}/>
            {label}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Financeiro({ currentUser }) {
  const { theme } = useTheme();
  const { de: deInit, ate: ateInit } = mesAtual();
  const [aba, setAba]             = useState("entradas");
  const [lancamentos, setLancs]   = useState([]);
  const [resumo, setResumo]       = useState(null);
  const [loading, setLoading]     = useState(true);
  const [modal, setModal]         = useState(null);
  const [tipoModal, setTipoModal] = useState("entrada");
  const [de, setDe]               = useState(deInit);
  const [ate, setAte]             = useState(ateInit);
  const [filtroStatus, setFiltroStatus] = useState("");

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const [lancsRes, resumoRes] = await Promise.all([
        api.get(`/financeiro?de=${de}&ate=${ate}`),
        api.get(`/financeiro/resumo?de=${de}&ate=${ate}`),
      ]);
      setLancs(lancsRes.data.lancamentos);
      setResumo(resumoRes.data);
    } catch {} finally { setLoading(false); }
  }, [de, ate]);

  useEffect(() => { carregar(); }, [carregar]);

  const salvar = async (form) => {
    if (form._deletar) {
      await api.delete(`/financeiro/${form._id}`);
      setLancs(prev => prev.filter(l => l._id !== form._id));
    } else if (form._id) {
      const { data } = await api.put(`/financeiro/${form._id}`, form);
      setLancs(prev => prev.map(l => l._id === data._id ? data : l));
    } else {
      const { data } = await api.post("/financeiro", form);
      setLancs(prev => [data, ...prev]);
    }
    setModal(null);
    carregar();
  };

  const marcarPago = async (id) => {
    const { data } = await api.patch(`/financeiro/${id}/pagar`);
    setLancs(prev => prev.map(l => l._id === id ? data : l));
    carregar();
  };

  const filtradas = lancamentos.filter(l => {
    const tipoOk = aba === "entradas" ? l.tipo === "entrada" : aba === "saidas" ? l.tipo === "saida" : true;
    const statusOk = !filtroStatus || l.status === filtroStatus;
    return tipoOk && statusOk;
  });

  const vencidos = lancamentos.filter(l => l.dataVencimento && new Date(l.dataVencimento) < new Date() && l.status === "pendente");

  const ABAS = [
    { id:"entradas",  label:"💰 A Receber",    cor:"#10b981" },
    { id:"saidas",    label:"💸 A Pagar",       cor:"#ef4444" },
    { id:"fluxo",     label:"📊 Fluxo de Caixa",cor:ORANGE    },
    { id:"dre",       label:"📋 DRE",           cor:"#6366f1" },
  ];

  return (
    <div style={{ fontFamily:"'Inter',-apple-system,sans-serif", background:theme.bg, minHeight:"100vh" }}>
      {/* Header */}
      <div style={{ padding:"20px 28px 0", background:theme.bgSecond, borderBottom:`1px solid ${theme.border}` }}>

        {/* Alertas de vencimento */}
        {vencidos.length > 0 && (
          <div style={{ background:"#ef444415", border:"1px solid #ef444433", borderRadius:10, padding:"10px 16px", marginBottom:14, fontSize:13, color:"#ef4444", display:"flex", alignItems:"center", gap:8 }}>
            ⚠️ <strong>{vencidos.length} lançamento{vencidos.length>1?"s":""}  vencido{vencidos.length>1?"s":""}</strong> — total {fmt(vencidos.reduce((a,l)=>a+l.valor,0))}
          </div>
        )}

        {/* Cards de resumo */}
        {resumo && (
          <div style={{ display:"flex", gap:12, marginBottom:16, flexWrap:"wrap" }}>
            {[
              ["A Receber",fmt(resumo.aReceber),"#f59e0b"],
              ["Recebido",fmt(resumo.totalRecebido),"#10b981"],
              ["A Pagar",fmt(resumo.aPagar),"#ef4444"],
              ["Pago",fmt(resumo.totalPago),"#8E8E93"],
              ["Lucro líquido",fmt(resumo.lucroLiquido),resumo.lucroLiquido>=0?"#10b981":"#ef4444"],
            ].map(([label,val,cor])=>(
              <div key={label} style={{ background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:12, padding:"12px 18px", flex:1, minWidth:120, boxShadow:theme.name==="light"?"0 1px 4px rgba(0,0,0,.06)":"none" }}>
                <div style={{ fontSize:11, color:theme.textMuted, fontWeight:500, marginBottom:4 }}>{label}</div>
                <div style={{ fontSize:18, fontWeight:800, color:cor, fontFamily:"'Inter',sans-serif" }}>{val}</div>
              </div>
            ))}
          </div>
        )}

        {/* Toolbar */}
        <div style={{ display:"flex", gap:10, alignItems:"center", marginBottom:16, flexWrap:"wrap" }}>
          {/* Abas */}
          <div style={{ display:"flex", background:theme.bgInput, borderRadius:10, padding:3, gap:2 }}>
            {ABAS.map(a=>(
              <button key={a.id} onClick={()=>setAba(a.id)} style={{ padding:"7px 14px", borderRadius:8, fontSize:12, fontWeight:aba===a.id?700:400, cursor:"pointer", background:aba===a.id?a.cor:"transparent", border:"none", color:aba===a.id?"#fff":theme.textMuted, transition:"all .15s" }}>{a.label}</button>
            ))}
          </div>

          {/* Período */}
          <div style={{ display:"flex", gap:6, alignItems:"center" }}>
            <span style={{ fontSize:12, color:theme.textMuted }}>De</span>
            <input type="date" value={de} onChange={e=>setDe(e.target.value)}
              style={{ background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:8, padding:"6px 10px", color:theme.text, fontSize:12, outline:"none", colorScheme:theme.name }}/>
            <span style={{ fontSize:12, color:theme.textMuted }}>até</span>
            <input type="date" value={ate} onChange={e=>setAte(e.target.value)}
              style={{ background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:8, padding:"6px 10px", color:theme.text, fontSize:12, outline:"none", colorScheme:theme.name }}/>
          </div>

          {/* Filtro status */}
          {(aba==="entradas"||aba==="saidas") && (
            <select value={filtroStatus} onChange={e=>setFiltroStatus(e.target.value)}
              style={{ background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:8, padding:"7px 10px", color:theme.text, fontSize:12, outline:"none", colorScheme:theme.name }}>
              <option value="">Todos os status</option>
              {Object.entries(STATUS).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
            </select>
          )}

          <div style={{ marginLeft:"auto", display:"flex", gap:8 }}>
            <button onClick={()=>{ setTipoModal("entrada"); setModal({}); }}
              style={{ background:"#10b98122", border:"1px solid #10b98144", borderRadius:10, padding:"8px 16px", color:"#10b981", fontWeight:700, fontSize:13, cursor:"pointer" }}>
              + Entrada
            </button>
            <button onClick={()=>{ setTipoModal("saida"); setModal({}); }}
              style={{ background:"#ef444422", border:"1px solid #ef444444", borderRadius:10, padding:"8px 16px", color:"#ef4444", fontWeight:700, fontSize:13, cursor:"pointer" }}>
              + Saída
            </button>
          </div>
        </div>
      </div>

      {/* Conteúdo */}
      <div style={{ padding:"20px 28px" }}>
        {loading ? (
          <div style={{ textAlign:"center", padding:60, color:theme.textMuted }}>Carregando...</div>
        ) : (
          <>
            {(aba==="entradas"||aba==="saidas") && (
              <TabelaLancamentos lancamentos={filtradas} onEdit={l=>{ setTipoModal(l.tipo); setModal(l); }} onPagar={marcarPago} tipo={aba==="entradas"?"entrada":"saida"}/>
            )}
            {aba==="fluxo" && (
              <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
                <GraficoFluxo dados={resumo?.fluxoMensal} theme={theme}/>
                {/* Categorias de despesa */}
                {resumo?.categorias && Object.keys(resumo.categorias).length > 0 && (
                  <div style={{ background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:12, padding:20 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:theme.text, marginBottom:14 }}>💸 Despesas por categoria</div>
                    {Object.entries(resumo.categorias).sort((a,b)=>b[1]-a[1]).map(([cat,val],i)=>{
                      const maxCat = Math.max(...Object.values(resumo.categorias));
                      const cores = ["#6366f1","#ef4444","#f59e0b","#3b82f6","#e879f9","#10b981","#8E8E93"];
                      const cor = cores[i%cores.length];
                      return (
                        <div key={cat} style={{ marginBottom:10 }}>
                          <div style={{ display:"flex", justifyContent:"space-between", fontSize:13, marginBottom:5 }}>
                            <span style={{ color:theme.textSub }}>{cat}</span>
                            <span style={{ color:cor, fontWeight:700 }}>{fmt(val)}</span>
                          </div>
                          <div style={{ background:theme.bgInput, borderRadius:99, height:8, overflow:"hidden" }}>
                            <div style={{ width:`${(val/maxCat)*100}%`, background:cor, height:"100%", borderRadius:99 }}/>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            {aba==="dre" && <DRE resumo={resumo} theme={theme}/>}
          </>
        )}
      </div>

      {modal !== null && (
        <ModalLancamento
          lanc={Object.keys(modal).length ? modal : null}
          tipo={tipoModal}
          onClose={()=>setModal(null)}
          onSave={salvar}
          clientes={[]}
        />
      )}
    </div>
  );
}
